import {
  pgTable,
  text,
  boolean,
  timestamp,
  integer,
  index,
  uniqueIndex,
  foreignKey,
  jsonb,
  numeric,
  primaryKey,
  check,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { bookFormat, bookProcessingStatus } from './enums';
import { file } from './files';
import { user } from './user';

/**
 * BookProgress.readPages payload — список прочитанных страниц + макс. достигнутая страница.
 * Используется только для текстовых форматов (EPUB/PDF/FB2). Для AUDIO — completedChapterIds.
 */
export interface BookReadPagesData {
  pages: number[];
  maxPage: number;
}

/**
 * BookProgress.currentLocator — текущая позиция чтения / прослушивания.
 * type определяет формат значения:
 *   - 'page'    → value: number (PDF/FB2)
 *   - 'cfi'     → value: string (EPUB Canonical Fragment Identifier)
 *   - 'seconds' → value: number (AUDIO, обязателен chapterId)
 */
export type BookCurrentLocator =
  | { type: 'page'; value: number; chapterId?: number }
  | { type: 'cfi'; value: string; chapterId?: number }
  | { type: 'seconds'; value: number; chapterId: number };

/**
 * BookBookmark.locator — позиция закладки в книге. Та же доменная модель,
 * что и BookCurrentLocator, поэтому используем общий тип.
 */
export type BookBookmarkLocator = BookCurrentLocator;

/**
 * Book — единая сущность для книг и аудиокниг.
 *
 * Один Book = одна книга в одном языке. Для переводов одной и той же книги
 * (разные языки источника) используется translationGroupId — общий UUID-литерал
 * (хранится текстом), связывающий несколько Book как переводы одного произведения.
 *
 * Метаданные с локализацией (title, description, authorDisplay) хранятся в
 * BookTranslation. Поле `author` на Book — fallback на случай отсутствия переводов.
 *
 * CHECK constraint гарантирует, что AUDIO имеет totalDurationSec, а текстовые
 * форматы — totalPages.
 */
export const book = pgTable('Book', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  slug: text().notNull(),
  format: bookFormat().notNull(),

  // Файлы
  coverFileId: integer(),
  sourceFileId: integer().notNull(),
  renderFileId: integer(), // для FB2 → сконвертированный EPUB

  // Метаданные (язык / автор fallback / связь между переводами)
  author: text(),
  languageCode: text().notNull(),
  translationGroupId: text(), // общий ключ для переводов одной книги

  // Объёмные характеристики
  totalPages: integer(),
  totalDurationSec: integer(),

  // Коммерция. FK объявлен только в SQL миграции (0045_library_books.sql) —
  // на уровне TS не объявляем, чтобы избежать циклической зависимости
  // book ↔ pricing-tier ↔ course.
  pricingTierId: integer(),

  // Публикация
  isPublished: boolean().default(false).notNull(),
  publishedAt: timestamp({ precision: 3, mode: 'date' }),

  // Обработка (конвертация / нарезка HLS и т.п.)
  processingStatus: bookProcessingStatus().default('PENDING').notNull(),
  processingError: text(),

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).$onUpdate(() => new Date()).notNull(),
}, (table) => [
  // Целостность размеров: AUDIO требует duration, остальные — totalPages
  check(
    'book_format_size_check',
    sql`(${table.format} = 'AUDIO' AND ${table.totalDurationSec} IS NOT NULL) OR (${table.format} <> 'AUDIO' AND ${table.totalPages} IS NOT NULL)`,
  ),
  uniqueIndex('Book_slug_key').using('btree', table.slug.asc().nullsLast()),
  index('Book_languageCode_idx').using('btree', table.languageCode.asc().nullsLast()),
  index('Book_isPublished_idx').using('btree', table.isPublished.asc().nullsLast()),
  index('Book_translationGroupId_idx').using('btree', table.translationGroupId.asc().nullsLast()),
  index('Book_format_idx').using('btree', table.format.asc().nullsLast()),
  index('Book_processingStatus_idx').using('btree', table.processingStatus.asc().nullsLast()),
  foreignKey({
    columns: [table.coverFileId],
    foreignColumns: [file.id],
    name: 'Book_coverFileId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.sourceFileId],
    foreignColumns: [file.id],
    name: 'Book_sourceFileId_fkey',
  }).onUpdate('cascade').onDelete('restrict'),
  foreignKey({
    columns: [table.renderFileId],
    foreignColumns: [file.id],
    name: 'Book_renderFileId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

/**
 * BookTranslation — переводы метаданных книги (название, описание, отображаемый автор).
 *
 * languageCode на Book хранит «основной» язык книги (для дефолтного fallback).
 * BookTranslation хранит переводы UI-текстов на разные языки.
 */
export const bookTranslation = pgTable('BookTranslation', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  bookId: integer().notNull(),
  languageCode: text().notNull(),
  title: text().notNull(),
  description: text(),
  authorDisplay: text(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).$onUpdate(() => new Date()).notNull(),
}, (table) => [
  uniqueIndex('BookTranslation_bookId_languageCode_key').using(
    'btree',
    table.bookId.asc().nullsLast(),
    table.languageCode.asc().nullsLast(),
  ),
  index('BookTranslation_bookId_idx').using('btree', table.bookId.asc().nullsLast()),
  foreignKey({
    columns: [table.bookId],
    foreignColumns: [book.id],
    name: 'BookTranslation_bookId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

/**
 * BookChapter — главы книги.
 *
 * Для AUDIO: каждая глава = аудиофайл (audioFileId) или HLS-плейлист.
 * Для текстовых форматов: глава задаёт диапазон pageStart..pageEnd (TOC).
 *
 * Уникальность по (bookId, order) гарантирует строгий порядок.
 */
export const bookChapter = pgTable('BookChapter', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  bookId: integer().notNull(),
  order: integer().notNull(),

  // AUDIO
  audioFileId: integer(),
  hlsPlaylistUrl: text(),
  hlsFileId: integer(),
  durationSec: integer(),

  // Текстовые форматы — диапазон страниц
  pageStart: integer(),
  pageEnd: integer(),

  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).$onUpdate(() => new Date()).notNull(),
}, (table) => [
  uniqueIndex('BookChapter_bookId_order_key').using(
    'btree',
    table.bookId.asc().nullsLast(),
    table.order.asc().nullsLast(),
  ),
  index('BookChapter_bookId_idx').using('btree', table.bookId.asc().nullsLast()),
  foreignKey({
    columns: [table.bookId],
    foreignColumns: [book.id],
    name: 'BookChapter_bookId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.audioFileId],
    foreignColumns: [file.id],
    name: 'BookChapter_audioFileId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.hlsFileId],
    foreignColumns: [file.id],
    name: 'BookChapter_hlsFileId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

/**
 * BookChapterTranslation — переводы названий глав.
 */
export const bookChapterTranslation = pgTable('BookChapterTranslation', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  chapterId: integer().notNull(),
  languageCode: text().notNull(),
  title: text().notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).$onUpdate(() => new Date()).notNull(),
}, (table) => [
  uniqueIndex('BookChapterTranslation_chapterId_languageCode_key').using(
    'btree',
    table.chapterId.asc().nullsLast(),
    table.languageCode.asc().nullsLast(),
  ),
  index('BookChapterTranslation_chapterId_idx').using('btree', table.chapterId.asc().nullsLast()),
  foreignKey({
    columns: [table.chapterId],
    foreignColumns: [bookChapter.id],
    name: 'BookChapterTranslation_chapterId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

/**
 * BookProgress — прогресс пользователя по книге (один на пару user-book).
 *
 * Для текстовых форматов используется readPages (множество страниц + maxPage).
 * Для AUDIO — completedChapterIds (массив id глав, прослушанных целиком).
 * currentLocator хранит текущую позицию (страница / CFI / секунды + глава).
 * percent — кешированное значение прогресса 0..100 (рассчитывается сервисом).
 */
export const bookProgress = pgTable('BookProgress', {
  userId: integer().notNull(),
  bookId: integer().notNull(),
  readPages: jsonb().$type<BookReadPagesData>().default({ pages: [], maxPage: 0 }).notNull(),
  completedChapterIds: jsonb().$type<number[]>().default([]).notNull(),
  /**
   * Per-chapter прогресс в секундах для AUDIO книг: ключ — chapterId,
   * значение — сколько секунд конкретной главы прослушано (0..durationSec).
   * Позволяет показывать прогресс по каждой главе, а не только по активной.
   */
  chapterPositions: jsonb().$type<Record<string, number>>().default({}).notNull(),
  currentLocator: jsonb().$type<BookCurrentLocator>(),
  percent: numeric({ precision: 5, scale: 2 }).default('0').notNull(),
  lastOpenedAt: timestamp({ precision: 3, mode: 'date' }),
  completedAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).$onUpdate(() => new Date()).notNull(),
}, (table) => [
  primaryKey({ columns: [table.userId, table.bookId], name: 'BookProgress_pkey' }),
  index('BookProgress_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('BookProgress_bookId_idx').using('btree', table.bookId.asc().nullsLast()),
  index('BookProgress_lastOpenedAt_idx').using('btree', table.lastOpenedAt.desc().nullsLast()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'BookProgress_userId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.bookId],
    foreignColumns: [book.id],
    name: 'BookProgress_bookId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

/**
 * BookChapterNote — индивидуальная заметка пользователя к главе книги.
 *
 * Много заметок на пару (user, chapter). Каждая заметка хранит
 * positionSec — позицию в секундах от начала главы, в которой она была
 * сделана. Позволяет вернуться к соответствующему моменту аудио-плеера.
 */
export const bookChapterNote = pgTable('BookChapterNote', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  userId: integer().notNull(),
  bookId: integer().notNull(),
  chapterId: integer().notNull(),
  content: text().notNull(),
  positionSec: integer().notNull().default(0),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).$onUpdate(() => new Date()).notNull(),
}, (table) => [
  index('BookChapterNote_userId_bookId_idx').using(
    'btree',
    table.userId.asc().nullsLast(),
    table.bookId.asc().nullsLast(),
  ),
  index('BookChapterNote_user_book_created_idx').using(
    'btree',
    table.userId.asc().nullsLast(),
    table.bookId.asc().nullsLast(),
    table.createdAt.desc().nullsLast(),
  ),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'BookChapterNote_userId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.bookId],
    foreignColumns: [book.id],
    name: 'BookChapterNote_bookId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.chapterId],
    foreignColumns: [bookChapter.id],
    name: 'BookChapterNote_chapterId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

/**
 * BookBookmark — пользовательская закладка в книге с опциональной заметкой.
 */
export const bookBookmark = pgTable('BookBookmark', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  userId: integer().notNull(),
  bookId: integer().notNull(),
  locator: jsonb().$type<BookBookmarkLocator>().notNull(),
  note: text(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).$onUpdate(() => new Date()).notNull(),
}, (table) => [
  index('BookBookmark_userId_bookId_idx').using(
    'btree',
    table.userId.asc().nullsLast(),
    table.bookId.asc().nullsLast(),
  ),
  index('BookBookmark_bookId_idx').using('btree', table.bookId.asc().nullsLast()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'BookBookmark_userId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.bookId],
    foreignColumns: [book.id],
    name: 'BookBookmark_bookId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);
